/**
 * Custom hook for password validation and strength requirements
 * Provides reusable password validation logic across components
 * 
 * @param {string} password - The password to validate
 * @param {string} confirmPassword - The confirmation password to match
 * @returns {Object} Validation state and helper functions
 */

import { useState, useEffect, useCallback } from 'react';
import { logInfo, logWarning } from '../utils/logger';

// Password requirement patterns
const PASSWORD_PATTERNS = {
  minLength: (password) => password.length >= 8,
  hasNumber: (password) => /\d/.test(password),
  hasUpper: (password) => /[A-Z]/.test(password),
  hasLower: (password) => /[a-z]/.test(password),
  hasSpecial: (password) => /[!@#$%^&*(),.?":{}|<>]/.test(password),
};

/**
 * Hook for password validation with real-time feedback
 */
export const usePasswordValidation = (password = '', confirmPassword = '') => {
  const [requirements, setRequirements] = useState({
    minLength: false,
    hasNumber: false,
    hasUpper: false,
    hasLower: false,
    hasSpecial: false,
    passwordsMatch: false,
  });

  const [strength, setStrength] = useState({
    score: 0,
    level: 'weak', // weak, fair, good, strong
    feedback: '',
  });

  /**
   * Calculate password strength based on requirements met
   */
  const calculateStrength = useCallback((reqs) => {
    const metRequirements = Object.values(reqs).filter(Boolean).length;
    const totalRequirements = Object.keys(reqs).length;
    const score = Math.round((metRequirements / totalRequirements) * 100);

    let level, feedback;
    
    if (score < 30) {
      level = 'weak';
      feedback = 'Password is too weak';
    } else if (score < 60) {
      level = 'fair';
      feedback = 'Password strength is fair';
    } else if (score < 85) {
      level = 'good';
      feedback = 'Password strength is good';
    } else {
      level = 'strong';
      feedback = 'Password is strong';
    }

    return { score, level, feedback };
  }, []);

  /**
   * Validate password against all requirements
   */
  const validatePassword = useCallback(() => {
    try {
      const newRequirements = {
        minLength: PASSWORD_PATTERNS.minLength(password),
        hasNumber: PASSWORD_PATTERNS.hasNumber(password),
        hasUpper: PASSWORD_PATTERNS.hasUpper(password),
        hasLower: PASSWORD_PATTERNS.hasLower(password),
        hasSpecial: PASSWORD_PATTERNS.hasSpecial(password),
        passwordsMatch: password === confirmPassword && password !== '',
      };

      setRequirements(newRequirements);

      // Calculate strength (excluding passwordsMatch for strength calculation)
      const strengthRequirements = { ...newRequirements };
      delete strengthRequirements.passwordsMatch;
      const strengthData = calculateStrength(strengthRequirements);
      setStrength(strengthData);

      // Log password validation attempts for monitoring
      if (password.length > 0) {
        logInfo('Password validation performed', {
          hasMinLength: newRequirements.minLength,
          hasComplexity: newRequirements.hasNumber && newRequirements.hasUpper && newRequirements.hasLower,
          strengthLevel: strengthData.level,
          score: strengthData.score
        });
      }

      // Log potential security concerns
      if (password.length > 50) {
        logWarning('Extremely long password attempted', { length: password.length });
      }

      return newRequirements;
    } catch (error) {
      logWarning('Password validation error', { error: error.message });
      // Return empty requirements object to prevent using stale state
      const defaultRequirements = {
        minLength: false,
        hasNumber: false,
        hasUpper: false,
        hasLower: false,
        hasSpecial: false,
        passwordsMatch: false,
      };
      return defaultRequirements;
    }
  }, [password, confirmPassword, calculateStrength]); // Removed 'requirements' dependency

  // Update requirements when password or confirmPassword changes
  useEffect(() => {
    validatePassword();
  }, [validatePassword]);

  /**
   * Check if password meets all requirements
   */
  const isPasswordValid = useCallback(() => {
    return Object.values(requirements).every(Boolean);
  }, [requirements]);

  /**
   * Check if password meets minimum security requirements (excluding confirmation)
   */
  const meetsMinimumRequirements = useCallback(() => {
    const { passwordsMatch, ...securityRequirements } = requirements;
    return Object.values(securityRequirements).every(Boolean);
  }, [requirements]);

  /**
   * Get password strength color for UI
   */
  const getStrengthColor = useCallback(() => {
    switch (strength.level) {
      case 'weak':
        return 'error';
      case 'fair':
        return 'warning';
      case 'good':
        return 'info';
      case 'strong':
        return 'success';
      default:
        return 'error';
    }
  }, [strength.level]);

  /**
   * Get requirement status for specific requirement
   */
  const getRequirementStatus = useCallback((requirementKey) => {
    return requirements[requirementKey] || false;
  }, [requirements]);

  /**
   * Get all failed requirements
   */
  const getFailedRequirements = useCallback(() => {
    return Object.entries(requirements)
      .filter(([_, isMet]) => !isMet)
      .map(([key]) => key);
  }, [requirements]);

  /**
   * Get human-readable requirement descriptions
   */
  const getRequirementDescriptions = useCallback(() => {
    return {
      minLength: 'At least 8 characters',
      hasNumber: 'Contains a number',
      hasUpper: 'Contains an uppercase letter',
      hasLower: 'Contains a lowercase letter',
      hasSpecial: 'Contains a special character',
      passwordsMatch: 'Passwords match',
    };
  }, []);

  return {
    requirements,
    strength,
    isPasswordValid,
    meetsMinimumRequirements,
    getStrengthColor,
    getRequirementStatus,
    getFailedRequirements,
    getRequirementDescriptions,
    validatePassword,
  };
};

export default usePasswordValidation; 